import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { cssVars } from "../theme";
import "./HLCInspire16.css";

import residential from "../assets/indoor/hlc-residential-indoor-lighting.webp";
import commercial from "../assets/indoor/hlc-commercial-indoor-lighting.webp";
import outdoor from "../assets/indoor/hlc-outdoor-lighting.webp";
import industrial from "../assets/indoor/hlc-industrial-lighting.webp";
import allLightsOn from "../assets/Night View - All Lights On.png";
import wallLightsOn from "../assets/Night View Only Wall Lights On.png";

const SPACES = [
  { image: residential, tag: "Residential", name: "Warm Living Room", meta: "Cozy ambient lighting" },
  { image: commercial, tag: "Commercial", name: "Modern Office Workspace", meta: "Glare-free task lighting" },
  { image: outdoor, tag: "Outdoor", name: "Garden & Pathway", meta: "Path & landscape accents" },
  { image: industrial, tag: "Industrial", name: "High-Bay Industrial Hall", meta: "High output, low glare" },
  { image: allLightsOn, tag: "Outdoor Living", name: "Outdoor Living Room", meta: "Layered outdoor lighting" },
  { image: wallLightsOn, tag: "Facade", name: "Accent Wall Lighting", meta: "Wall washers & accents" },
];

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const pad = (n) => String(n).padStart(2, "0");

const slideVariants = {
  enter: { opacity: 0, scale: 1.06, y: 22 },
  active: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, scale: 1.04, y: -14, transition: { duration: 0.5, ease: "easeIn" } },
};

/* Decorative hotspot only — there is no per-image product/fixture data to
   connect precisely, so this surfaces general, already-established site
   claims (same phrasing used in the Top Selling / Calculator benefit
   strips) rather than inventing per-space specs. */
function Hotspot() {
  const [open, setOpen] = useState(false);
  return (
    <div className="hlc-inspire16__hotspot" onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className="hlc-inspire16__hotdot"
        aria-label="View HLC LED lighting details"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
      >
        <span className="hlc-inspire16__hotpulse" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="hlc-inspire16__hotcard"
            initial={{ opacity: 0, scale: 0.92, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 6 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <strong>HLC LED</strong>
            <ul>
              <li>High Brightness</li>
              <li>Energy Efficient</li>
            </ul>
            <Link to="/shop">Explore Product →</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function HLCInspire16() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(true);
  const stageRef = useRef(null);

  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const parallaxX = useTransform(rotateY, [-1, 1], [-10, 10]);
  const parallaxY = useTransform(rotateX, [-1, 1], [-8, 8]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isDesktop) return undefined;

    function onScroll() {
      const el = stageRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      if (total <= 0) return;
      const progressed = Math.min(Math.max(-rect.top, 0), total);
      const idx = Math.min(SPACES.length - 1, Math.floor((progressed / total) * SPACES.length));
      setActiveIndex((prev) => (prev === idx ? prev : idx));
    }

    let frame = null;
    const schedule = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => { frame = null; onScroll(); });
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [isDesktop]);

  function jumpTo(i) {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const targetScroll = window.scrollY + rect.top + total * (i / SPACES.length) + 4;
    window.scrollTo({ top: targetScroll, behavior: REDUCED_MOTION ? "instant" : "smooth" });
  }

  function handleStageMouseMove(e) {
    if (REDUCED_MOTION) return;
    const rect = e.currentTarget.getBoundingClientRect();
    rotateY.set(((e.clientX - rect.left) / rect.width - 0.5) * 2);
    rotateX.set(((e.clientY - rect.top) / rect.height - 0.5) * 2);
  }
  function handleStageMouseLeave() {
    rotateX.set(0);
    rotateY.set(0);
  }

  const active = SPACES[activeIndex];

  return (
    <section id="hlc-inspire16" className="hlc-inspire16" aria-label="Lighting Inspiration Gallery" style={cssVars}>
      <div className="hlc-inspire16__inner">
        <header className="hlc-inspire16__head">
          <p className="hlc-inspire16__eyebrow">Lighting Inspiration</p>
          <h2 className="hlc-inspire16__title">See HLC Lighting in Real Spaces</h2>
          <p className="hlc-inspire16__sub">
            Explore how HLC LEDs transform living rooms, offices, storefronts and outdoor spaces.
          </p>
        </header>

        {isDesktop ? (
          <div className="hlc-inspire16__scrollstage" ref={stageRef} style={{ height: `${(SPACES.length + 1) * 100}vh` }}>
            <div className="hlc-inspire16__sticky">
              <div className="hlc-inspire16__progress">
                <span className="hlc-inspire16__count">{pad(activeIndex + 1)} / {pad(SPACES.length)}</span>
                <div className="hlc-inspire16__bar">
                  {SPACES.map((s, i) => (
                    <button
                      key={s.name}
                      type="button"
                      className={`hlc-inspire16__seg${i <= activeIndex ? " is-filled" : ""}${i === activeIndex ? " is-active" : ""}`}
                      aria-label={`Go to ${s.tag}: ${s.name}`}
                      onClick={() => jumpTo(i)}
                    />
                  ))}
                </div>
              </div>

              <div
                className="hlc-inspire16__stage"
                onMouseMove={handleStageMouseMove}
                onMouseLeave={handleStageMouseLeave}
              >
                <AnimatePresence>
                  <motion.div
                    key={activeIndex}
                    className="hlc-inspire16__slide"
                    variants={slideVariants}
                    initial="enter"
                    animate="active"
                    exit="exit"
                  >
                    <motion.img
                      src={active.image}
                      alt={active.name}
                      style={REDUCED_MOTION ? undefined : { x: parallaxX, y: parallaxY }}
                      animate={REDUCED_MOTION ? undefined : { scale: [1, 1.045] }}
                      transition={REDUCED_MOTION ? undefined : { duration: 9, ease: "linear" }}
                    />
                    {!REDUCED_MOTION && <motion.div className="hlc-inspire16__sweep" initial={{ x: "-120%" }} animate={{ x: "120%" }} transition={{ duration: 1.1, ease: "easeInOut", delay: 0.1 }} />}
                    <div className="hlc-inspire16__vignette" />
                    <Hotspot />
                    <div className="hlc-inspire16__caption">
                      <span className="hlc-inspire16__tag">{active.tag}</span>
                      <h3>{active.name}</h3>
                      <p>{active.meta}</p>
                      <Link to="/shop" className="hlc-inspire16__slideCta">Explore HLC Lighting →</Link>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        ) : (
          <div className="hlc-inspire16__mobilelist">
            {SPACES.map((s, i) => (
              <motion.article
                key={s.name}
                className="hlc-inspire16__mobileitem"
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, ease: "easeOut" }}
              >
                <div className="hlc-inspire16__mobilemedia">
                  <img src={s.image} alt={s.name} loading="lazy" />
                  <div className="hlc-inspire16__vignette" />
                  <span className="hlc-inspire16__mobilecount">{pad(i + 1)} / {pad(SPACES.length)}</span>
                </div>
                <div className="hlc-inspire16__caption hlc-inspire16__caption--static">
                  <span className="hlc-inspire16__tag">{s.tag}</span>
                  <h3>{s.name}</h3>
                  <p>{s.meta}</p>
                  <Link to="/shop" className="hlc-inspire16__slideCta">Explore HLC Lighting →</Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <div className="hlc-inspire16__ctaRow">
          <Link to="/shop" className="hlc-inspire16__btn hlc-inspire16__btn--primary">Shop the Look</Link>
          <Link to="/contact" className="hlc-inspire16__btn hlc-inspire16__btn--ghost">Request Lighting Plan</Link>
        </div>
      </div>
    </section>
  );
}
